import React, { useEffect, useMemo, useRef, useState } from 'react';

export const COUNTRIES = [
  { code: 'PE', flag: '🇵🇪', dialCode: '+51', name: 'Perú',           groups: [3, 3, 3], maxLen: 9  },
  { code: 'CL', flag: '🇨🇱', dialCode: '+56', name: 'Chile',          groups: [1, 4, 4], maxLen: 9  },
  { code: 'CO', flag: '🇨🇴', dialCode: '+57', name: 'Colombia',       groups: [3, 3, 4], maxLen: 10 },
  { code: 'AR', flag: '🇦🇷', dialCode: '+54', name: 'Argentina',      groups: [2, 4, 4], maxLen: 10 },
  { code: 'MX', flag: '🇲🇽', dialCode: '+52', name: 'México',         groups: [2, 4, 4], maxLen: 10 },
  { code: 'US', flag: '🇺🇸', dialCode: '+1',  name: 'Estados Unidos', groups: [3, 3, 4], maxLen: 10 },
  { code: 'ES', flag: '🇪🇸', dialCode: '+34', name: 'España',         groups: [3, 3, 3], maxLen: 9  },
];
export const DEFAULT_COUNTRY = COUNTRIES[0];
// Longest dial codes first so "+51" isn't accidentally matched as "+1".
const COUNTRIES_BY_DIAL_DESC = [...COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length
);

export function formatDigitGroups(digits, groups) {
  const parts = [];
  let i = 0;
  for (const g of groups) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + g));
    i += g;
  }
  if (i < digits.length) parts.push(digits.slice(i));
  return parts.join(' ');
}

export function parseStoredPhone(stored) {
  if (!stored) return { country: DEFAULT_COUNTRY, digits: '' };
  const cleaned = String(stored).replace(/\s+/g, '');
  const noPlus = cleaned.replace(/^\+/, '');
  for (const c of COUNTRIES_BY_DIAL_DESC) {
    const dialNoPlus = c.dialCode.replace(/^\+/, '');
    if (noPlus.startsWith(dialNoPlus)) {
      return { country: c, digits: noPlus.slice(dialNoPlus.length).replace(/\D/g, '') };
    }
  }
  return { country: DEFAULT_COUNTRY, digits: noPlus.replace(/\D/g, '') };
}

export function buildStoredPhone(country, digits) {
  if (!digits) return '';
  const formatted = formatDigitGroups(digits, country.groups);
  return `${country.dialCode} ${formatted}`;
}

export function formatPhoneDisplay(stored) {
  if (!stored) return '';
  const { country, digits } = parseStoredPhone(stored);
  if (!digits) return stored;
  return `${country.flag} ${country.dialCode} ${formatDigitGroups(digits, country.groups)}`;
}

export function PhoneField({ label, value, onChange, labelStyle, fieldGroupStyle, autoFocus }) {
  const { country, digits } = useMemo(() => parseStoredPhone(value), [value]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const telInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') { setOpen(false); setSearch(''); }
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q) ||
      c.code.toLowerCase().includes(q)
    );
  }, [search]);

  function pickCountry(c) {
    onChange(buildStoredPhone(c, digits));
    setOpen(false);
    setSearch('');
    // Keep focus inside the field so wrappers using focus-within / blur
    // detection don't think the user has left the control.
    setTimeout(() => telInputRef.current?.focus(), 0);
  }

  function onTypePhone(e) {
    const raw = e.target.value.replace(/\D/g, '').slice(0, country.maxLen);
    onChange(buildStoredPhone(country, raw));
  }

  const formattedValue = formatDigitGroups(digits, country.groups);
  const placeholder = formatDigitGroups(''.padEnd(country.maxLen, '9'), country.groups);

  return (
    <label style={{ ...styles.fieldGroup, ...(fieldGroupStyle || {}) }}>
      {label && <span style={{ ...styles.fieldLabel, ...(labelStyle || {}) }}>{label}</span>}
      <div style={styles.phoneRow} ref={wrapRef}>
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          style={styles.phoneCountryBtn}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span style={styles.phoneFlag}>{country.flag}</span>
          <span style={styles.phoneDial}>{country.dialCode}</span>
          <span style={styles.phoneCaret} aria-hidden="true">▾</span>
        </button>
        <input
          ref={telInputRef}
          type="tel"
          inputMode="numeric"
          autoFocus={autoFocus}
          value={formattedValue}
          onChange={onTypePhone}
          placeholder={placeholder}
          style={styles.phoneNumberInput}
        />
        {open && (
          <div style={styles.phoneDropdown} role="listbox">
            <div style={styles.phoneDropdownSearchWrap}>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar país..."
                style={styles.phoneDropdownSearch}
                aria-label="Buscar país"
              />
            </div>
            <div style={styles.phoneDropdownList}>
              {filteredCountries.length === 0 ? (
                <div style={styles.phoneDropdownEmpty}>Sin resultados</div>
              ) : (
                filteredCountries.map(c => {
                  const active = c.code === country.code;
                  return (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => pickCountry(c)}
                      style={{
                        ...styles.phoneDropdownItem,
                        ...(active ? styles.phoneDropdownItemActive : {}),
                      }}
                      role="option"
                      aria-selected={active}
                    >
                      <span style={styles.phoneFlag}>{c.flag}</span>
                      <span style={styles.phoneDropdownName}>{c.name}</span>
                      <span style={styles.phoneDropdownDial}>{c.dialCode}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </label>
  );
}

const styles = {
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.02em' },
  phoneRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
    width: '100%',
  },
  phoneCountryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 10px',
    borderRadius: '8px 0 0 8px',
    border: '1px solid var(--border)',
    borderRight: 'none',
    background: '#FDFBF8',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  phoneFlag: { fontSize: 16, lineHeight: 1 },
  phoneDial: { fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  phoneCaret: { fontSize: 10, color: 'var(--text-light)', marginLeft: 2 },
  phoneNumberInput: {
    flex: 1,
    minWidth: 0,
    padding: '9px 12px',
    borderRadius: '0 8px 8px 0',
    border: '1px solid var(--border)',
    background: '#FDFBF8',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    letterSpacing: '0.02em',
    fontVariantNumeric: 'tabular-nums',
  },
  phoneDropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    width: 260,
    maxWidth: '100%',
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
    zIndex: 2000,
    overflow: 'hidden',
  },
  phoneDropdownSearchWrap: {
    padding: 8,
    borderBottom: '1px solid var(--border)',
    background: '#FDFBF8',
  },
  phoneDropdownSearch: {
    width: '100%',
    padding: '7px 10px',
    borderRadius: 6,
    border: '1px solid var(--border)',
    background: '#fff',
    fontSize: 12,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
  },
  phoneDropdownList: { maxHeight: 240, overflowY: 'auto' },
  phoneDropdownEmpty: {
    padding: '16px 12px', textAlign: 'center', color: 'var(--text-light)', fontSize: 12,
  },
  phoneDropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    textAlign: 'left',
  },
  phoneDropdownItemActive: { background: 'rgba(107,28,42,0.08)' },
  phoneDropdownName: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  phoneDropdownDial: { color: 'var(--text-light)', fontVariantNumeric: 'tabular-nums' },
};
